import React, { cloneElement } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { merge, $ } from "glamor";
import { get } from "lodash";
import shallowequal from "shallowequal";

import theme from "styles/theme";
import { themeOptions } from "styles/theme";
import DragHandle from "../drag-handle";
import Actions from "../item/actions";
import BulkSelector from "../item/bulk-selector";
import ActionMenu from "../item/action-menu";
import EditProductModal from "components/product-edit/product-edit-modal";
import { DATATEST } from "constants/integration";
import BulkDragBadge from "../item/bulk-drag-badge";
import BulkDragPlaceholder from "../item/bulk-drag-placeholder";
import BulkDragPreview from "../item/bulk-drag-preview";
import me from "models/me";
import { gtm } from "helpers/gtm";

const styles = merge(
    {
        display: "block",
        padding: "0px",
        position: "relative",
    },
    $(" .item", {
        cursor: "pointer",
        position: "absolute",
        top: "5px",
        left: "5px",
        margin: "0px",
        transition: "all .2s ease-in-out",
        backgroundColor: "white",
        zIndex: 2,
    }),
    $(" .item.card", {
        boxShadow: "0 2px 2px 0 rgba(0,0,0,.14), 0 3px 1px -2px rgba(0,0,0,.2), 0 1px 5px 0 rgba(0,0,0,.12)",
    }),
    $(" .item.itemHover", {
        zIndex: 3,
        boxShadow: "0 2px 2px 0 rgba(0,0,0,.14), 0 3px 1px -2px rgba(0,0,0,.2), 0 1px 5px 0 rgba(0,0,0,.12)",
    }),
    $(" .item.card.itemHover", {
        boxShadow: "rgba(0, 0, 0, 0.16) 0px 2px 6px, rgba(0, 0, 0, 0.24) 0px 3px 6px",
    }),
    $(" .item.active", {
        boxShadow: "rgba(0, 0, 0, 0.16) 0px 2px 6px, rgba(0, 0, 0, 0.24) 0px 3px 6px",
    }),
    $(" .item.disabled", {
        opacity: 0.5,
    }),
    $(" .item.list", {
        borderBottom: `1px solid ${themeOptions.palette.borderColor}`,
        position: "absolute",
        top: "0px",
        left: "0px",
        margin: "0px",
        transition: "all .2s ease-in-out",
        backgroundColor: "white",
        zIndex: 2,
        display: "inline-flex",
    }),
    $(" .item.active.list", {
        boxShadow: "none",
    }),
    $(" .item.list.header", {
        cursor: "default",
    }),
    $(" .item.list.draggable", {
        paddingLeft: "40px",
    }),
    $(" .item.list.draggableListHeader", {
        paddingLeft: "40px",
    }),
    $(" .item.bulkPreview", {
        zIndex: 4,
    }),
    $(" .item.list.bulkPreview", {
        boxShadow: "0 2px 2px 0 rgba(0,0,0,.14), 0 3px 1px -2px rgba(0,0,0,.2), 0 1px 5px 0 rgba(0,0,0,.12)",
    }),
    $(" .item.bulkPreview.isDragging", {
        display: "none",
    }),
    $(" .item.bulkReorderEntry", {
        display: "none",
    })
);

const mStyles = {
    deleteFab: {
        position: "absolute",
        top: "4px",
        right: "4px",
        zIndex: 5,
    },
};

const excludeUpdateCheck = ["style", "lib/app"];

export default function (Component) {
    class BaseItemViewComponent extends Component {
        constructor(props) {
            super(props);
            this.state = this.state || {};
            this.state.hover = false;
            this.state.inActiveList = props.item && props.activeList && props.activeList.entries.has(props.item._id);
            this.state.inOrder = props.item && props.cart && props.cart.items.has(props.item._id);
            this.state.showEditModal = false;

            this.handleItemEnter = this.handleItemEnter.bind(this);
            this.handleItemLeave = this.handleItemLeave.bind(this);

            this.handleDragStart = this.handleDragStart.bind(this);
            this.handleDisableBulkPreview = this.handleDisableBulkPreview.bind(this);

            this.handleItemClick = this.handleItemClick.bind(this);
            this.handleItemMoreInfo = this.handleItemMoreInfo.bind(this);

            this.handleSwatchSelected = this.handleSwatchSelected.bind(this);

            this.handleDelete = this.handleDelete.bind(this);

            this.handleNotesIconClick = this.handleNotesIconClick.bind(this);

            this.handleCloseEditModal = this.handleCloseEditModal.bind(this);
        }

        UNSAFE_componentWillReceiveProps(nextProps, nextState, nextContext) {
            if (
                nextProps.item !== this.props.item ||
                (nextProps.activeList !== this.props.activeList && nextProps.activeList) ||
                (nextProps.cart !== this.props.cart && nextProps.cart)
            ) {
                this.setState({
                    inActiveList: nextProps.item && nextProps.activeList?.entries.has(nextProps.item._id),
                    inOrder: nextProps.item && nextProps.cart && nextProps.cart.items.has(nextProps.item._id),
                });
            }
            if (super.UNSAFE_componentWillReceiveProps) {
                super.UNSAFE_componentWillReceiveProps(nextProps, nextState, nextContext);
            }
        }

        shouldComponentUpdate(nextProps, nextState) {
            if (this.props.view !== nextProps.view) {
                return true;
            }

            if (!nextProps.placeholder && !nextProps.view) {
                return false;
            }

            if (nextProps.style.top !== this.props.style.top || nextProps.style.left !== this.props.style.left) {
                return true;
            }

            const hasUpdates = Object.keys(nextProps)
                .filter((key) => !excludeUpdateCheck.includes(key))
                .some((key) => nextProps[key] !== this.props[key]);

            if (hasUpdates) {
                return true;
            }
            return !shallowequal(this.state, nextState);
        }

        getShipStart() {
            return get(this.props, "catalogDates.ship_start");
        }

        handleSwatchSelected(e, item) {
            const { onItemClick } = this.props.actions;
            if (onItemClick) {
                onItemClick(item);
            }
        }

        handleItemClick(e) {
            const { actions, item, catalog, scope } = this.props;
            if (item && actions.onItemClick) {
                gtm.emit(
                    gtm.events.viewItem({
                        currency: me._activeCurrency,
                        value: gtm.fetchValueFromItems([item], scope),
                        items: [gtm.formatItem(item, scope, catalog?.type, catalog?.id, catalog?.title)],
                        send_to: gtm.groups.Brand,
                    })
                );
                gtm.emit(
                    gtm.events.pageView({
                        page_location: `${window.location.protocol}//${window.location.host}/item/${item?.name}/${gtm.fetchItemBrandId(item)}`,
                        page_title: `item: ${item?.name}/${gtm.fetchItemBrandId(item)}`,
                        send_to: gtm.groups.Brand,
                    })
                );
                actions.onItemClick(item);
            }
        }

        handleItemMoreInfo(e) {
            const { actions, item } = this.props;
            e.preventDefault();
            e.stopPropagation();
            if (item && actions.onMoreInfo) {
                actions.onMoreInfo(item);
            }
        }

        handleItemEnter() {
            const { item } = this.props;
            if (item && window.matchMedia("(pointer:fine)").matches) {
                this.setState({ hover: true });
            }
        }

        handleItemLeave() {
            this.setState({ hover: false });
        }

        handleDragStart() {
            if (
                this.props.bulkItems?.size > 1 &&
                this.props.bulkItems.includes(this.props.catalogEntry?.id ?? this.props.item?._id)
            ) {
                this.setState({ bulkPreview: true });
            }
        }

        handleDisableBulkPreview() {
            this.setState({ bulkPreview: false });
        }

        handleNotesIconClick(e) {
            e.stopPropagation();
            const { actions, catalogEntry } = this.props;
            const { onViewNotes } = actions;
            if (onViewNotes) {
                onViewNotes(catalogEntry.id);
            }
        }

        renderBulkSelector() {
            const { actions, bulkItems, item, catalogEntry, viewNotes, view, list, hideBulkActions } = this.props;
            const { hover } = this.state;
            const { onBulkItemSelectionChange } = actions;
            const showImage = view && view.sections.get("small_image");

            if (item && bulkItems && !viewNotes && !hideBulkActions) {
                return (
                    <BulkSelector
                        hover={hover || !window.matchMedia("(pointer:fine)").matches || (list && !showImage)}
                        bulkItems={bulkItems}
                        value={catalogEntry ? catalogEntry : item}
                        onChange={onBulkItemSelectionChange}
                        list={list}
                    />
                );
            } else {
                return null;
            }
        }

        productActionMenu() {
            const { item, editable, catalogEntry, dnd, viewNotes, view, list, scope } = this.props;
            const { hover } = this.state;
            const showImage = view && view.sections.get("small_image");

            return (
                <ActionMenu
                    hover={hover || !window.matchMedia("(pointer:fine)").matches || (list && !showImage)}
                    isDeletable={catalogEntry && editable && hover && dnd !== "dragable" && !viewNotes}
                    onDelete={this.handleDelete}
                    value={catalogEntry ?? item}
                    list={list}
                    onEditProduct={this.handleEditProduct}
                    onEditNotes={this.handleNotesIconClick}
                    activeBrand={scope?.brand}
                    editable={editable}
                    catalogEntry={catalogEntry}
                    dnd={dnd}
                    viewNotes={viewNotes}
                />
            );
        }
        renderActions(visibleButtons = 3, isTile = false) {
            const {
                actions,
                item,
                catalogEntry,
                catalog,
                editable,
                activeList,
                cart,
                isActiveList,
                onSnackbarAdd,
                scope,
                list,
                actionsOffset,
            } = this.props;
            const { hover, inOrder, inActiveList } = this.state;
            return (
                <Actions
                    key="actions"
                    hover={hover}
                    onDelete={this.handleDelete}
                    inOrder={inOrder}
                    inActiveList={inActiveList}
                    item={item}
                    catalogEntry={catalogEntry}
                    catalogDates={this.props.catalogDates}
                    catalog={catalog}
                    editable={editable}
                    onRequestShowNotes={this.handleNotesIconClick}
                    actions={actions}
                    activeList={activeList}
                    visibleButtons={visibleButtons}
                    isActiveList={isActiveList}
                    cart={cart}
                    onSnackbarAdd={onSnackbarAdd}
                    scope={scope}
                    listView={list}
                    actionsOffset={actionsOffset}
                    isTile={isTile}
                    onEditProduct={this.handleEditProduct}
                />
            );
        }

        handleDelete(e) {
            e.preventDefault();
            e.stopPropagation();
            const { catalogEntry, actions } = this.props;
            actions.onDelete(catalogEntry);
            /*
            const { catalogEntry, item, onSnackbarAdd } = this.props;
            (catalogEntry || item).destroy({
                success: () => {
                    onSnackbarAdd({ message: Globalize.formatMessage('Item removed') });
                }
            });
            */
        }

        handleEditProduct = () => {
            this.setState({ showEditModal: true });
        };

        handleCloseEditModal() {
            this.setState({ showEditModal: false });
        }

        render() {
            const {
                style,
                placeholder,
                isDragging,
                item,
                card,
                list,
                activeItemId,
                disabled,
                touchDevice,
                dnd,
                connectDragSource,
                scope,
                bulkItems,
                catalogEntry,
                bulkDragInsert,
                bulkDragReorder,
            } = this.props;
            const { hover, bulkPreview } = this.state;

            const dragHandlerStyle = list ? { top: "8px" } : { left: "46px", top: "12px" };
            const draggable = (item || catalogEntry) && ["dragable", "both"].includes(dnd) && touchDevice;
            const draggableListHeader = list && !item && ["dragable", "both"].includes(dnd) && touchDevice;

            const bulkReorderEntry = bulkDragInsert && catalogEntry?.options?.reorder;
            const isSelected = bulkItems?.includes(catalogEntry?.id ?? item?._id);

            let content = null;
            if (!placeholder) {
                content = cloneElement(super.render(), {
                    onClick: this.handleItemClick,
                    onMouseEnter: this.handleItemEnter,
                    onMouseLeave: this.handleItemLeave,
                    "data-test": DATATEST.GALLERY.ITEM_TILE,
                    "data-test-item-id": item?._id,
                    className: classNames("item", {
                        itemHover: hover,
                        disabled:
                            disabled ||
                            (item &&
                                (!item.isAvailable(scope) ||
                                    (item._disabled && !scope.brand.hasPreference("hide_currency_warning")))),
                        card,
                        list,
                        draggable,
                        draggableListHeader,
                        active: item && item._id === activeItemId,
                        header: list && !item,
                        bulkPreview,
                        isDragging,
                        bulkReorderEntry,
                    }),
                });
            }

            content = (
                <div
                    data-test={DATATEST.GALLERY.ITEM_CARD}
                    {...styles}
                    {...Component.styles}
                    style={Object.assign(
                        {},
                        style,
                        isDragging && !bulkPreview && { opacity: 0 },
                        bulkPreview && { margin: "12px", zIndex: 9, overflow: "visible" },
                        bulkDragReorder && isSelected && !bulkPreview && { opacity: 0.28 }
                    )}
                    className="baseItemViewComponent"
                    onDragStart={this.handleDragStart}
                    onDragEnd={this.handleDisableBulkPreview}
                    onMouseUp={this.handleDisableBulkPreview}
                >
                    {draggable && <DragHandle connectDragSource={connectDragSource} style={dragHandlerStyle} />}
                    {bulkPreview && !isDragging && (
                        <>
                            <BulkDragPreview card={card} list={list} />
                            <BulkDragBadge
                                card={card}
                                list={list}
                                value={
                                    bulkItems.allSelected
                                        ? this.props.results.length - bulkItems.list.size
                                        : bulkItems.size
                                }
                            />
                        </>
                    )}
                    {((bulkPreview && isDragging) || bulkReorderEntry) && (
                        <BulkDragPlaceholder color="#6f9ec6" list={list} />
                    )}
                    {content}
                    {this.state.showEditModal && (
                        <EditProductModal
                            item={item}
                            onCloseModal={this.handleCloseEditModal}
                            scope={scope}
                            onSnackbarAdd={this.props.onSnackbarAdd}
                            onEntriesRefresh={this.props.onEntriesRefresh}
                            schemas={this.props.schemas}
                        />
                    )}
                </div>
            );

            return ["dragable", "both"].includes(dnd) && touchDevice ? this.props.connectDragPreview(content) : content;
        }
    }

    BaseItemViewComponent.contextTypes = {
        ...BaseItemViewComponent.contextTypes,
        me: PropTypes.object.isRequired,
    };

    BaseItemViewComponent.propTypes = {
        ...BaseItemViewComponent.propTypes,
        item: PropTypes.object,
        catalogEntry: PropTypes.object,
        catalog: PropTypes.object,
        actions: PropTypes.object,
        editable: PropTypes.bool,
        style: PropTypes.object,
        view: PropTypes.object,
        bulkItems: PropTypes.object,
        viewNotes: PropTypes.bool,
        activeList: PropTypes.object,
        card: PropTypes.bool,
        list: PropTypes.bool,
        cart: PropTypes.object,
        activeItemId: PropTypes.string,
        disabled: PropTypes.bool,
        isActiveList: PropTypes.bool,
        onSnackbarAdd: PropTypes.func,
        scope: PropTypes.object.isRequired,
        showActions: PropTypes.bool,
        hideBulkActions: PropTypes.bool,
        onEntriesRefresh: PropTypes.func,
        schemas: PropTypes.object,
        showActionMenu: PropTypes.bool,
    };

    BaseItemViewComponent.defaultProps = {
        ...BaseItemViewComponent.defaultProps,
        editable: false,
        viewNotes: false,
        style: {},
        actions: {},
        card: false,
        list: false,
        disabled: false,
        isActiveList: false,
        showActions: true,
        showActionMenu: true,
    };

    return BaseItemViewComponent;
}
